import logo from "./logo.svg";
import "./App.css";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { ssoExchange } from "./Components/API_Service/Login_Slice";
import SecureAccessPage from "./Components/Modules/LogIn/SecureAccessPage";
import UserManagementPage from "./Components/Modules/UserManagement/UserManagementPage";
import DropdownConfigIndex from "./Components/Modules/ApplicationConfig/DropdownConfigIndex";
import ApplicationConfigPage from "./Components/Modules/ApplicationConfig/ApplicationConfigPage";
import DropdownConfigCreate from "./Components/Modules/ApplicationConfig/DropdownConfigCreate";
import { GlobalLoaderProvider } from "./Components/Common/GlobalLoader";
import TestTypeConfigIndex from "./Components/Modules/ApplicationConfig/TestType/TestTypeConfigIndex";
import TestTypeConfigCreate from "./Components/Modules/ApplicationConfig/TestType/TestTypeConfigCreate";
import FacilityConfigIndex from "./Components/Modules/ApplicationConfig/Facility/FacilityConfigIndex";
import FacilityConfigCreate from "./Components/Modules/ApplicationConfig/Facility/FacilityConfigCreate";
import RolemangementPage from "./Components/Modules/RoleManagement/RoleManagementPage";
import CreateEditRolePage from "./Components/Modules/RoleManagement/CreateEditRolePage";
import CreateEditUserPage from "./Components/Modules/UserManagement/CreateEditUserPage";
import LocationConfigIndex from "./Components/Modules/ApplicationConfig/Location/LocationConfigIndex";
import LocationConfigCreate from "./Components/Modules/ApplicationConfig/Location/LocationConfigCreate";
import ShiftMappingPage from "./Components/Modules/LabAssociatehiftMapping/ShiftMappingPage";
import CreateEditShiftMappingPage from "./Components/Modules/LabAssociatehiftMapping/CreateEditShiftMappingPage";
import HolidayCalendarIndex from "./Components/Modules/ApplicationConfig/HolidayCalendar/HolidayCalendarIndex";
import ShiftConfigIndex from "./Components/Modules/ApplicationConfig/Shift/ShiftConfigIndex";
import Toast from "./Components/Common/Toast";
import EmailConfig from "./Components/Modules/ApplicationConfig/GeneralConfiguration/EmailConfig";
import CredentialsConfig from "./Components/Modules/ApplicationConfig/GeneralConfiguration/CredentialsConfig";
import GeneralConfigIndex from "./Components/Modules/ApplicationConfig/GeneralConfiguration/GeneralConfigIndex";
import LabBookingIndex from "./Components/Modules/LabMangement/LabBookingIndex";
import ProjectMangement from "./Components/Modules/ProjectManagement/ProjectListPage";
import CreateEditProjectMangement from "./Components/Modules/ProjectManagement/CreateEditProjectPage";
import LabBookingRequest from "./Components/Modules/LabMangement/LabBookingRequest";
import LabUtilizationIndex from "./Components/Modules/LabMangement/LabUtilizationIndex";
import LabUtilizationCreate from "./Components/Modules/LabMangement/LabUtilizationCreate";
import SetupUtilLocations from "./Components/Modules/LabMangement/SetupUtilLocations";
import ApiClient from "./Components/Modules/LogIn/ApiClient";
import PrivateRoute from "./Components/Common/PrivateRoute";
import LabUtilizationReport from "./Components/Modules/Reports/LabUtilizationReport";
import EmailTemplateIndex from "./Components/Modules/EmailTemplate/EmailTemplateIndex";
import EmailTemplateCreate from "./Components/Modules/EmailTemplate/EmailTemplateCreate";
import UnauthorizedPage from "./Components/Common/UnauthorizedPage";
import { PRIVILEGES } from "./Components/Common/UIComponents";
import useSessionTimeout from "../src/Components/Common/useSessionTimeout";
import { hasPrivilege } from "./Components/Common/UIComponents";
import HappinessIndex from "./Components/Modules/HappinessIndex/HappinessIndex";
import DashboardMain from "./Components/Modules/Dashboard/DashboardMain";
import NetworkStatusBanner from "./Components/Common/NetworkStatusBanner";
import CheckListConfigCreate from "./Components/Modules/ApplicationConfig/CheckList/CheckListConfigCreate";
import MPListIndex from "./Components/Modules/MeasurementProtocol/MPListIndex";
import ShiftChangeOverReport from "./Components/Modules/Reports/ShiftChangeOverReport";
import FacilityBookingScheduler from "./Components/Modules/LabMangement/FacilityBookingScheduler";

// Shorthand helpers for common privilege groups
const EMP = [
  PRIVILEGES.EMP_VIEW,
  PRIVILEGES.EMP_CREATE,
  PRIVILEGES.EMP_EDIT,
  PRIVILEGES.EMP_DELETE,
];
const ROLE = [
  PRIVILEGES.ROLE_VIEW,
  PRIVILEGES.ROLE_CREATE,
  PRIVILEGES.ROLE_EDIT,
  PRIVILEGES.ROLE_DELETE,
];
const PROJ = [
  PRIVILEGES.PROJ_VIEW,
  PRIVILEGES.PROJ_CREATE,
  PRIVILEGES.PROJ_EDIT,
];
const LAB = [PRIVILEGES.LAB_VIEW];
const SU = [PRIVILEGES.SU_VIEW, PRIVILEGES.SU_CREATE, PRIVILEGES.SU_EDIT];
const EMAIL = [PRIVILEGES.EMAIL_VIEW, PRIVILEGES.EMAIL_EDIT];
const CFG = ["APPCONFIGVIEW"];

function ModalRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const background = location.state && location.state.backgroundLocation;
  useSessionTimeout({
    onTimeout: () => {
      window.location.href = "/login";
    },
  });

  // ── Smile SSO handoff: capture ?sso=<code> before any redirect logic runs ──
  const [ssoProcessing, setSsoProcessing] = useState(
    () => new URLSearchParams(window.location.search).get("sso") != null,
  );

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("sso");
    if (!code) return;

    dispatch(ssoExchange(code))
      .unwrap()
      .then((data) => {
        window.history.replaceState(null, "", window.location.pathname);
        setSsoProcessing(false);
        if (data?.isUserExists && data?.isRoleExists) {
          navigate("/", { replace: true });
        } else {
          navigate("/login", { replace: true });
        }
      })
      .catch(() => {
        window.history.replaceState(null, "", window.location.pathname);
        setSsoProcessing(false);
        navigate("/login", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (ssoProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-[#08757C] animate-spin" />
      </div>
    );
  }

  const getDefaultRoute = () => {
    const token = localStorage.getItem("token");
    if (!token) return "/login";
    // Embedded-from-Smile sessions only have Lab Management in the sidebar —
    // send them straight there instead of the usual Associate/User Management page.
    if (localStorage.getItem("isFromSmile") === "1")
      return "/lab-management/lab-Booking";
    if (EMP.some((p) => hasPrivilege(p))) return "/user-management";
    if (ROLE.some((p) => hasPrivilege(p))) return "/role-management";
    if (PROJ.some((p) => hasPrivilege(p))) return "/project-management";
    if (LAB.some((p) => hasPrivilege(p))) return "/lab-management/lab-Booking";
    if (SU.some((p) => hasPrivilege(p)))
      return "/lab-management/lab-utilization";
    if (EMAIL.some((p) => hasPrivilege(p))) return "/email-templateindex";
    return "/unauthorized";
  };

  return (
    <>
      <Toast />
      <Routes location={background || location}>
        {/* Public */}
        <Route path="/login" element={<SecureAccessPage />} />
        <Route path="/ApiClient" element={<ApiClient />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Default redirect — goes to first accessible page */}
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardMain />
            </PrivateRoute>
          }
        />

        {/* ── User Management ── */}
        <Route
          path="/user-management"
          element={
            <PrivateRoute privilege={EMP}>
              <UserManagementPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/user-management/create"
          element={
            <PrivateRoute privilege={[PRIVILEGES.EMP_CREATE]}>
              <CreateEditUserPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/user-management/edit/:id"
          element={
            <PrivateRoute privilege={[PRIVILEGES.EMP_EDIT]}>
              <CreateEditUserPage />
            </PrivateRoute>
          }
        />

        {/* ── Role Management ── */}
        <Route
          path="/role-management"
          element={
            <PrivateRoute privilege={ROLE}>
              <RolemangementPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/role-management/create"
          element={
            <PrivateRoute privilege={[PRIVILEGES.ROLE_CREATE]}>
              <CreateEditRolePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/role-management/edit/:id"
          element={
            <PrivateRoute privilege={[PRIVILEGES.ROLE_EDIT]}>
              <CreateEditRolePage />
            </PrivateRoute>
          }
        />

        {/* ── Project Management ── */}
        <Route
          path="/project-management"
          element={
            <PrivateRoute privilege={PROJ}>
              <ProjectMangement />
            </PrivateRoute>
          }
        />
        <Route
          path="/project-management/create"
          element={
            <PrivateRoute privilege={[PRIVILEGES.PROJ_CREATE]}>
              <CreateEditProjectMangement />
            </PrivateRoute>
          }
        />
        <Route
          path="/project-management/edit/:id"
          element={
            <PrivateRoute privilege={[PRIVILEGES.PROJ_EDIT]}>
              <CreateEditProjectMangement />
            </PrivateRoute>
          }
        />

        {/* ── Application Config ── */}
        <Route
          path="/application-config"
          element={
            <PrivateRoute>
              <ApplicationConfigPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/dropdown"
          element={
            <PrivateRoute privilege={CFG}>
              <DropdownConfigIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/dropdown/create"
          element={
            <PrivateRoute privilege={CFG}>
              <DropdownConfigCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/test-type"
          element={
            <PrivateRoute privilege={CFG}>
              <TestTypeConfigIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/test-type/create"
          element={
            <PrivateRoute privilege={CFG}>
              <TestTypeConfigCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/facilities"
          element={
            <PrivateRoute privilege={CFG}>
              <FacilityConfigIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/facilities/create"
          element={
            <PrivateRoute privilege={CFG}>
              <FacilityConfigCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/location"
          element={
            <PrivateRoute privilege={CFG}>
              <LocationConfigIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/location/create"
          element={
            <PrivateRoute privilege={CFG}>
              <LocationConfigCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/holiday-calendar"
          element={
            <PrivateRoute privilege={CFG}>
              <HolidayCalendarIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/shift"
          element={
            <PrivateRoute privilege={CFG}>
              <ShiftConfigIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/email-configuration"
          element={
            <PrivateRoute privilege={CFG}>
              <EmailConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/credentials"
          element={
            <PrivateRoute privilege={CFG}>
              <CredentialsConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-config/general"
          element={
            <PrivateRoute privilege={CFG}>
              <GeneralConfigIndex />
            </PrivateRoute>
          }
        />

        <Route
          path="/application-config/checklist/create"
          element={
            <PrivateRoute privilege={CFG}>
              <CheckListConfigCreate />
            </PrivateRoute>
          }
        />

        {/* ── Shift Mapping ── */}
        <Route
          path="/shift-mapping"
          element={
            <PrivateRoute privilege={EMP}>
              <ShiftMappingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/shift-mapping/create"
          element={
            <PrivateRoute privilege={[PRIVILEGES.EMP_CREATE]}>
              <CreateEditShiftMappingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/shift-mapping/edit/:id"
          element={
            <PrivateRoute privilege={[PRIVILEGES.EMP_EDIT]}>
              <CreateEditShiftMappingPage />
            </PrivateRoute>
          }
        />

        {/* ── Lab Management ── */}
        <Route
          path="/lab-management/lab-Booking"
          element={
            <PrivateRoute privilege={LAB}>
              <LabBookingIndex />
            </PrivateRoute>
          }
        />

        <Route path="/lab-management/facility-booking" element={
          
           <PrivateRoute privilege={LAB}>
             <FacilityBookingScheduler />
           </PrivateRoute>
         } />
        <Route
          path="/lab-management/labBooking-requests"
          element={
            <PrivateRoute
              privilege={[
                PRIVILEGES.LAB_VIEW,
                PRIVILEGES.SU_VIEW,
                PRIVILEGES.SU_EDIT,
              ]}
            >
              <LabBookingRequest />
            </PrivateRoute>
          }
        />
        <Route
          path="/lab-management/setup-lab-utilization"
          element={
            <PrivateRoute privilege={SU}>
              <SetupUtilLocations />
            </PrivateRoute>
          }
        />
        <Route
          path="/lab-management/lab-utilization"
          element={
            <PrivateRoute privilege={SU}>
              <LabUtilizationIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/lab-utilization/create"
          element={
            <PrivateRoute privilege={[PRIVILEGES.SU_CREATE]}>
              <LabUtilizationCreate />
            </PrivateRoute>
          }
        />

        <Route
          path="/lab-management/measurement-protocol"
          element={
            <PrivateRoute>
              <MPListIndex />
            </PrivateRoute>
          }
        />

        {/* ── Reports ── */}
        <Route
          path="/reports/lab-utilization"
          element={
            <PrivateRoute privilege={[...LAB, ...SU]}>
              <LabUtilizationReport />
            </PrivateRoute>
          }
        />

        <Route
          path="/reports/shift-changeover"
          element={
            <PrivateRoute privilege={[...LAB, ...SU]}>
              <ShiftChangeOverReport/>
            </PrivateRoute>
          }
        />

        {/* ── Email Template ── */}
        <Route
          path="/email-templateindex"
          element={
            <PrivateRoute privilege={EMAIL}>
              <EmailTemplateIndex />
            </PrivateRoute>
          }
        />
        <Route
          path="/email-templateindex/create"
          element={
            <PrivateRoute privilege={[PRIVILEGES.EMAIL_EDIT]}>
              <EmailTemplateCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/email-templateindex/edit"
          element={
            <PrivateRoute privilege={[PRIVILEGES.EMAIL_EDIT]}>
              <EmailTemplateCreate />
            </PrivateRoute>
          }
        />
        <Route path="/happiness-index" element={<HappinessIndex />} />
        <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
      </Routes>

      {background && (
        <Routes>
          <Route
            path="/application-config/dropdown/create"
            element={
              <PrivateRoute privilege={CFG}>
                <DropdownConfigCreate />
              </PrivateRoute>
            }
          />

          <Route
            path="/application-config/checklist/create"
            element={
              <PrivateRoute privilege={CFG}>
                <CheckListConfigCreate />
              </PrivateRoute>
            }
          />
        </Routes>
      )}
    </>
  );
}

function App() {
  return (
    <GlobalLoaderProvider>
      <BrowserRouter>
        <NetworkStatusBanner />
        <ModalRoutes />
      </BrowserRouter>
    </GlobalLoaderProvider>
  );
}

export default App;
